import { Anchor, Button, Input, Modal, Toast } from '@byecode/ui'
import { CopyButton, ErrorMessage, TagIcon, useAtomAction, useAtomData } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useAsyncFn } from 'react-use'

import { fetchOtherPlatformsAtom } from '@/atoms/application/action'
import { otherPlatformListAtom } from '@/atoms/application/state'
import {
    otherPlatformBackgroundColorMap,
    otherPlatformColorMap,
    otherPlatformDescribeMap,
    otherPlatformIconMap,
    otherPlatformNameMap
} from '@/containers/OtherPlatforms/constant'
import * as srv from '@/services'

import { PLACEHOLDER_PASSWORD } from '../constants'
import * as SC from '../styles'
import type { ErnieBotFormState } from './type'

interface ErnieBotAccountProps {
    onClose?: () => void
}

const initData: ErnieBotFormState = {
    id: '',
    apiKey: '',
    secretKey: ''
}
const ErnieBotAccount: React.FunctionComponent<ErnieBotAccountProps> = ({ onClose }) => {
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)

    const account = useMemo(() => {
        const otherPlatform = find(item => item.type === 'ERNIE_BOT', otherPlatforms ?? [])
        return otherPlatform?.configurationComplete
            ? { ...otherPlatform?.config, id: otherPlatform.id, apiKey: PLACEHOLDER_PASSWORD, secretKey: PLACEHOLDER_PASSWORD }
            : { ...initData, id: otherPlatform?.id }
    }, [otherPlatforms])

    const {
        control,
        register,
        handleSubmit,
        getValues,
        formState: { errors }
    } = useForm({
        mode: 'onSubmit',
        defaultValues: account
    })

    const [{ loading: submitLoading }, doUpdateIntegration] = useAsyncFn(srv.updateIntegration)

    const handleUpdate = useCallback(
        async (params: ErnieBotFormState) => {
            const { id, apiKey, secretKey } = params
            if (!id || submitLoading) {
                return
            }
            const res = await doUpdateIntegration({
                type: 'ERNIE_BOT',
                id,
                config: {
                    apiKey: apiKey === PLACEHOLDER_PASSWORD ? undefined : apiKey,
                    secretKey: secretKey === PLACEHOLDER_PASSWORD ? undefined : secretKey
                }
            })
            if (res) {
                fetchOtherPlatforms()
                onClose?.()
            }
        },
        [onClose, submitLoading, fetchOtherPlatforms, doUpdateIntegration]
    )

    const handleInviteSubmit = useCallback(
        (ev: React.FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleUpdate)()
        },
        [handleSubmit, handleUpdate]
    )

    return (
        <SC.Container onSubmit={handleInviteSubmit}>
            <SC.Content>
                <TagIcon
                    size={48}
                    iconSize={32}
                    icon={otherPlatformIconMap['ERNIE_BOT']}
                    radius={6}
                    iconColor={otherPlatformColorMap['ERNIE_BOT']}
                    background={otherPlatformBackgroundColorMap['ERNIE_BOT']}
                />
                <SC.Title>{otherPlatformNameMap['ERNIE_BOT']}</SC.Title>
                <SC.Item>
                    <SC.Description>{otherPlatformDescribeMap['ERNIE_BOT']}</SC.Description>
                </SC.Item>
                <SC.Tip>
                    <SC.Icon color="var(--color-main)" type="WarningCircle" />
                    <SC.Text>如果您完善授权时遇到问题，请查看帮助文档：</SC.Text>
                    <Anchor target="_blank" style={{ fontWeight: 600, color: 'var(--color-main)' }} to="">
                        文心一言授权配置
                    </Anchor>
                </SC.Tip>

                <SC.Item>
                    <SC.Text>
                        API Key
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="apiKey" errors={errors}>
                        <Controller
                            name="apiKey"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        size="lg"
                                        autoComplete="new-password"
                                        type="password"
                                        placeholder="请填入API Key"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        {/* <Anchor target="_blank" to="" style={{ color: 'var(--color-main)' }}>
                            如何获取？
                        </Anchor> */}
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        Secret Key
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="secretKey" errors={errors}>
                        <Controller
                            name="secretKey"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        size="lg"
                                        autoComplete="new-password"
                                        type="password"
                                        placeholder="请填入API Key"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        {/* <Anchor target="_blank" to="" style={{ color: 'var(--color-main)' }}>
                            如何获取？
                        </Anchor> */}
                    </SC.Description>
                </SC.Item>
            </SC.Content>
            <SC.Footer>
                <SC.LeftFill />
                <SC.RightFill>
                    <Button size="lg" style={{ width: 104, height: 40 }} onClick={() => onClose?.()}>
                        取消
                    </Button>
                    <Button size="lg" loading={submitLoading} style={{ width: 104, height: 40 }} type="primary" htmlType="submit">
                        确定
                    </Button>
                </SC.RightFill>
            </SC.Footer>
        </SC.Container>
    )
}

export default ErnieBotAccount
